import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const FuelConsumptionData = ({ onFuelDataChange, fuelData }) => {
  const [formData, setFormData] = useState({
    mainEngineFuelType: '',
    mainEngineFuelConsumption: '',
    auxiliaryFuelType: '',
    auxiliaryFuelConsumption: '',
    boilerFuelType: '',
    boilerFuelConsumption: '',
    seaSpeed: '',
    portStayDuration: '',
    ballastConsumption: '',
    ladenConsumption: ''
  });

  const [iotSensorData, setIotSensorData] = useState({
    fuelTankLevel: 85.2,
    fuelFlowRate: 12.5,
    engineLoad: 78.3,
    lastUpdate: '1 min ago',
    sensorStatus: 'active'
  });

  const [noonReportData, setNoonReportData] = useState([
    {
      date: '2024-09-20',
      position: '52°22\'N, 04°54\'E',
      speed: 14.2,
      consumption: 24.8,
      weather: 'Fair',
      seaState: 3
    },
    {
      date: '2024-09-19',
      position: '51°58\'N, 03°12\'E',
      speed: 13.8,
      consumption: 23.2,
      weather: 'Good',
      seaState: 2
    }
  ]);

  const fuelTypes = [
    { value: 'vlsfo', label: 'VLSFO (0.5% S)' },
    { value: 'lsfo', label: 'LSFO (0.1% S)' },
    { value: 'mgo', label: 'MGO' },
    { value: 'hfo', label: 'HFO (3.5% S)' },
    { value: 'lng', label: 'LNG' },
    { value: 'methanol', label: 'Methanol' },
    { value: 'ammonia', label: 'Ammonia' }
  ];

  useEffect(() => {
    if (fuelData) {
      setFormData(fuelData);
    }
  }, [fuelData]);

  const handleInputChange = (field, value) => {
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);
    onFuelDataChange(updatedData);
  };

  const handleAutoFetchFuelData = () => {
    // Simulate auto-fetch from IoT sensors and noon reports
    const mockData = {
      mainEngineFuelType: 'vlsfo',
      mainEngineFuelConsumption: '24.5',
      auxiliaryFuelType: 'mgo',
      auxiliaryFuelConsumption: '3.2',
      boilerFuelType: 'vlsfo',
      boilerFuelConsumption: '1.8',
      seaSpeed: '14.2',
      portStayDuration: '18',
      ballastConsumption: '22.1',
      ladenConsumption: '26.8'
    };

    setFormData(prev => ({ ...prev, ...mockData }));
    onFuelDataChange({ ...formData, ...mockData });
  };

  const calculateEmissionFactor = (fuelType) => {
    const factors = {
      'vlsfo': 3.151,
      'lsfo': 3.151,
      'mgo': 3.206,
      'hfo': 3.114,
      'lng': 2.750,
      'methanol': 1.375,
      'ammonia': 0.000
    };
    return factors?.[fuelType] || 3.151;
  };

  return (
    <div className="maritime-card p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Fuel Consumption Data</h2>
          <p className="text-sm text-muted-foreground">
            Real-time data from IoT sensors and noon reports
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={handleAutoFetchFuelData}
          iconName="Database"
          iconPosition="left"
        >
          Fetch Sensor Data
        </Button>
      </div>
      {/* IoT Sensor Status */}
      <div className="mb-6 p-4 bg-success/10 rounded-lg border border-success/20">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-foreground flex items-center space-x-2">
            <Icon name="Cpu" size={16} className="text-success" />
            <span>IoT Fuel Sensors</span>
          </h3>
          <span className="text-xs text-success">Live Data</span>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-lg font-semibold text-foreground">{iotSensorData?.fuelTankLevel}%</div>
            <div className="text-xs text-muted-foreground">Tank Level</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-foreground">{iotSensorData?.fuelFlowRate} MT/h</div>
            <div className="text-xs text-muted-foreground">Flow Rate</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-foreground">{iotSensorData?.engineLoad}%</div>
            <div className="text-xs text-muted-foreground">Engine Load</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-foreground">{iotSensorData?.lastUpdate}</div>
            <div className="text-xs text-muted-foreground">Last Update</div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Main Engine */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground">Main Engine</h3>
          
          <Select
            label="Fuel Type"
            placeholder="Select fuel type"
            options={fuelTypes}
            value={formData?.mainEngineFuelType}
            onChange={(value) => handleInputChange('mainEngineFuelType', value)}
            required
          />

          <Input
            label="Consumption (MT/day)"
            type="number"
            step="0.1"
            placeholder="Enter consumption"
            value={formData?.mainEngineFuelConsumption}
            onChange={(e) => handleInputChange('mainEngineFuelConsumption', e?.target?.value)}
            description={`Emission Factor: ${calculateEmissionFactor(formData?.mainEngineFuelType)} tCO₂/MT`}
            required
          />

          <Input
            label="Sea Speed (knots)"
            type="number"
            step="0.1"
            placeholder="Average speed"
            value={formData?.seaSpeed}
            onChange={(e) => handleInputChange('seaSpeed', e?.target?.value)}
            required
          />
        </div>

        {/* Auxiliary Engine */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground">Auxiliary Engine</h3>
          
          <Select
            label="Fuel Type"
            placeholder="Select fuel type"
            options={fuelTypes}
            value={formData?.auxiliaryFuelType}
            onChange={(value) => handleInputChange('auxiliaryFuelType', value)}
            required
          />

          <Input
            label="Consumption (MT/day)"
            type="number"
            step="0.1"
            placeholder="Enter consumption"
            value={formData?.auxiliaryFuelConsumption}
            onChange={(e) => handleInputChange('auxiliaryFuelConsumption', e?.target?.value)}
            description={`Emission Factor: ${calculateEmissionFactor(formData?.auxiliaryFuelType)} tCO₂/MT`}
            required
          />

          <Input
            label="Port Stay Duration (hours)"
            type="number"
            placeholder="Hours in port"
            value={formData?.portStayDuration}
            onChange={(e) => handleInputChange('portStayDuration', e?.target?.value)}
            required
          />
        </div>

        {/* Boiler */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground">Boiler</h3>
          
          <Select
            label="Fuel Type"
            placeholder="Select fuel type"
            options={fuelTypes}
            value={formData?.boilerFuelType}
            onChange={(value) => handleInputChange('boilerFuelType', value)}
          />

          <Input
            label="Consumption (MT/day)"
            type="number"
            step="0.1"
            placeholder="Enter consumption"
            value={formData?.boilerFuelConsumption}
            onChange={(e) => handleInputChange('boilerFuelConsumption', e?.target?.value)}
            description={`Emission Factor: ${calculateEmissionFactor(formData?.boilerFuelType)} tCO₂/MT`}
          />
        </div>

        {/* Voyage Conditions */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground">Voyage Conditions</h3>
          
          <Input
            label="Ballast Consumption (MT/day)"
            type="number"
            step="0.1"
            placeholder="Ballast leg consumption"
            value={formData?.ballastConsumption}
            onChange={(e) => handleInputChange('ballastConsumption', e?.target?.value)}
          />

          <Input
            label="Laden Consumption (MT/day)"
            type="number"
            step="0.1"
            placeholder="Laden leg consumption"
            value={formData?.ladenConsumption}
            onChange={(e) => handleInputChange('ladenConsumption', e?.target?.value)}
          />
        </div>
      </div>
      {/* Recent Noon Reports */}
      <div className="mt-6">
        <h3 className="text-lg font-medium text-foreground mb-4">Recent Noon Reports</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 text-muted-foreground">Date</th>
                <th className="text-left py-2 text-muted-foreground">Position</th>
                <th className="text-left py-2 text-muted-foreground">Speed (kts)</th>
                <th className="text-left py-2 text-muted-foreground">Consumption (MT)</th>
                <th className="text-left py-2 text-muted-foreground">Weather</th>
                <th className="text-left py-2 text-muted-foreground">Sea State</th>
              </tr>
            </thead>
            <tbody>
              {noonReportData?.map((report, index) => (
                <tr key={index} className="border-b border-border/50">
                  <td className="py-2 text-foreground">{report?.date}</td>
                  <td className="py-2 text-foreground font-data text-xs">{report?.position}</td>
                  <td className="py-2 text-foreground">{report?.speed}</td>
                  <td className="py-2 text-foreground">{report?.consumption}</td>
                  <td className="py-2 text-foreground">{report?.weather}</td>
                  <td className="py-2 text-foreground">{report?.seaState}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FuelConsumptionData;